// Pure, runtime-agnostic audit-diff helpers. No Deno/Bun/DB/React imports, so
// the SAME logic backs both the browser audit writer (src/lib/audit.ts) and the
// server-side writer (queries.ts recordAuditServer). This keeps the
// `{ field: { old, new } }` change shape identical across every surface.

export type AuditOperation = "INSERT" | "UPDATE" | "DELETE";

export interface FieldChange {
  old: unknown;
  new: unknown;
}

export type AuditChanges = Record<string, FieldChange>;

// Columns that are never meaningful in a changelog.
const IGNORED_FIELDS = new Set(["created_at", "updated_at"]);

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Structural compare so arrays (weekly_hours_allocated, role[]) and objects
  // are compared by value rather than reference.
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Compute the changed fields between an old and new row (for UPDATE).
 * Ignores created_at/updated_at and unchanged fields.
 */
export function diffRecords(
  oldRow: Record<string, unknown> | null | undefined,
  newRow: Record<string, unknown> | null | undefined,
): AuditChanges {
  const changes: AuditChanges = {};
  const keys = new Set([...Object.keys(oldRow ?? {}), ...Object.keys(newRow ?? {})]);
  for (const key of keys) {
    if (IGNORED_FIELDS.has(key)) continue;
    const oldVal = oldRow?.[key] ?? null;
    const newVal = newRow?.[key] ?? null;
    if (!valuesEqual(oldVal, newVal)) {
      changes[key] = { old: oldVal, new: newVal };
    }
  }
  return changes;
}

/**
 * Build the per-field change shape for an INSERT (old=null) or DELETE (new=null)
 * from a full row. Keeps the uniform {old,new} shape the renderer expects.
 */
export function fullRowChanges(
  row: Record<string, unknown>,
  operation: "INSERT" | "DELETE",
): AuditChanges {
  const changes: AuditChanges = {};
  for (const [key, value] of Object.entries(row)) {
    if (IGNORED_FIELDS.has(key)) continue;
    if (operation === "INSERT") {
      changes[key] = { old: null, new: value ?? null };
    } else {
      changes[key] = { old: value ?? null, new: null };
    }
  }
  return changes;
}
